import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import type {
  ConversationAttachment,
  ConversationLink,
  ConversationListEntry,
} from '../../types';
import {
  IconArrowLeft,
  IconChevronRight,
  IconClose,
  IconDownload,
  IconFile,
  IconLink,
  IconSearch,
} from '../Icon';

interface InfoMediaProps {
  conversation: ConversationListEntry;
}

type Tab = 'media' | 'links' | 'docs';

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function dateGroupKey(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameMonth = d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  if (sameMonth) return 'This month';
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  if (d.getMonth() === lastMonth.getMonth() && d.getFullYear() === lastMonth.getFullYear()) {
    return 'Last month';
  }
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
}

function urlHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export function InfoMedia({ conversation }: InfoMediaProps) {
  const [tab, setTab] = useState<Tab>('media');
  const [media, setMedia] = useState<ConversationAttachment[] | null>(null);
  const [docs, setDocs] = useState<ConversationAttachment[] | null>(null);
  const [links, setLinks] = useState<ConversationLink[] | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [filter, setFilter] = useState('');

  const previewing = previewIndex !== null && media ? media[previewIndex] ?? null : null;

  // Reset the filter when the user switches tabs so a stale query doesn't
  // hide everything in the new tab.
  useEffect(() => {
    setFilter('');
  }, [tab]);

  function matchesFilter(text: string): boolean {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return text.toLowerCase().includes(q);
  }

  // Arrow keys navigate; Esc closes. Listener only attaches while the
  // lightbox is open so it doesn't leak.
  useEffect(() => {
    if (previewIndex === null || !media) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPreviewIndex(null);
      } else if (e.key === 'ArrowRight') {
        setPreviewIndex((i) => (i === null ? null : Math.min(media.length - 1, i + 1)));
      } else if (e.key === 'ArrowLeft') {
        setPreviewIndex((i) => (i === null ? null : Math.max(0, i - 1)));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewIndex, media]);

  useEffect(() => {
    if (tab === 'media' && media === null) {
      void api.conversationAttachments(conversation.id, 'media').then((r) => setMedia(r.attachments)).catch(() => setMedia([]));
    }
    if (tab === 'docs' && docs === null) {
      void api.conversationAttachments(conversation.id, 'docs').then((r) => setDocs(r.attachments)).catch(() => setDocs([]));
    }
    if (tab === 'links' && links === null) {
      void api.conversationLinks(conversation.id).then((r) => setLinks(r.links)).catch(() => setLinks([]));
    }
  }, [tab, conversation.id, media, docs, links]);

  const groupedMedia = useMemo(() => {
    if (!media) return null;
    const filtered = media.filter((m) => matchesFilter(m.filename));
    const groups = new Map<string, ConversationAttachment[]>();
    for (const m of filtered) {
      const k = dateGroupKey(m.createdAt);
      const arr = groups.get(k) ?? [];
      arr.push(m);
      groups.set(k, arr);
    }
    return [...groups.entries()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, filter]);

  return (
    <div>
      {/* Tabs */}
      <div className="px-4 pt-4 pb-2 sticky top-0 bg-zinc-950 z-10 border-b border-zinc-900 space-y-2">
        <div className="grid grid-cols-3 gap-1 rounded-md border border-zinc-800 p-1 bg-zinc-900/50">
          {(['media', 'links', 'docs'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-2 py-1.5 rounded text-xs font-medium transition capitalize ${
                tab === t ? 'bg-zinc-950 text-zinc-100 shadow ring-1 ring-zinc-700' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="relative">
          <span className="absolute top-1/2 -translate-y-1/2 left-2.5 text-zinc-500">
            <IconSearch size={12} />
          </span>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={
              tab === 'media'
                ? 'Search media by filename…'
                : tab === 'links'
                  ? 'Search links…'
                  : 'Search documents…'
            }
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 pl-7 pr-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Media grid grouped by date */}
      {tab === 'media' && (
        <div className="px-4 py-4">
          {media === null && <p className="text-xs text-zinc-500">Loading…</p>}
          {media && media.length === 0 && (
            <p className="text-xs text-zinc-500 py-6 text-center">No media yet.</p>
          )}
          {groupedMedia?.map(([heading, items]) => (
            <div key={heading} className="mb-5 last:mb-0">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{heading}</p>
              <div className="grid grid-cols-3 gap-1">
                {items.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => {
                      const i = media?.findIndex((x) => x.id === m.id) ?? -1;
                      if (i !== -1) setPreviewIndex(i);
                    }}
                    className="aspect-square bg-zinc-900 rounded overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-indigo-500 transition"
                  >
                    {m.contentType.startsWith('image/') ? (
                      <img
                        src={api.attachmentUrl(m.id)}
                        alt={m.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <video
                        src={api.attachmentUrl(m.id)}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Links */}
      {tab === 'links' && (
        <div className="px-4 py-4 space-y-2">
          {links === null && <p className="text-xs text-zinc-500">Loading…</p>}
          {links && links.length === 0 && (
            <p className="text-xs text-zinc-500 py-6 text-center">No links shared yet.</p>
          )}
          {links?.filter((l) => matchesFilter(l.url)).map((l, idx) => (
            <a
              key={`${l.messageId}-${idx}`}
              href={l.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-900 hover:border-zinc-700 transition"
            >
              <span className="shrink-0 text-zinc-400">
                <IconLink size={14} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate text-zinc-100">{urlHostname(l.url)}</p>
                <p className="text-[11px] text-zinc-500 truncate">{l.url}</p>
              </div>
              <span className="text-[10px] text-zinc-600 shrink-0">
                {new Date(l.createdAt).toLocaleDateString()}
              </span>
            </a>
          ))}
        </div>
      )}

      {/* Docs */}
      {tab === 'docs' && (
        <div className="px-4 py-4 space-y-2">
          {docs === null && <p className="text-xs text-zinc-500">Loading…</p>}
          {docs && docs.length === 0 && (
            <p className="text-xs text-zinc-500 py-6 text-center">No documents shared yet.</p>
          )}
          {docs?.filter((d) => matchesFilter(d.filename)).map((d) => (
            <a
              key={d.id}
              href={api.attachmentUrl(d.id, true)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-900 hover:border-zinc-700 transition"
            >
              <span className="shrink-0 text-zinc-400">
                <IconFile size={16} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{d.filename}</p>
                <p className="text-[11px] text-zinc-500">
                  {formatBytes(d.byteSize)} · {new Date(d.createdAt).toLocaleDateString()}
                </p>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Lightbox preview */}
      {previewing && previewIndex !== null && media && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col animate-fade-up"
          onClick={() => setPreviewIndex(null)}
        >
          <header className="px-5 py-3 flex items-center justify-between gap-3 shrink-0 text-zinc-100">
            <div className="min-w-0">
              <p className="text-sm truncate">{previewing.filename}</p>
              <p className="text-[11px] text-zinc-500">
                {previewIndex + 1} of {media.length} · {new Date(previewing.createdAt).toLocaleString()}
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-2">
              <a
                href={api.attachmentUrl(previewing.id, true)}
                download
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-zinc-300 hover:text-white px-2 py-1 rounded-md hover:bg-white/10 transition flex items-center gap-1.5"
              >
                <IconDownload size={12} />
                Download
              </a>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewIndex(null);
                }}
                aria-label="Close"
                className="w-9 h-9 rounded-xl text-zinc-300 hover:text-white hover:bg-white/10 transition flex items-center justify-center"
              >
                <IconClose size={14} />
              </button>
            </div>
          </header>

          <div
            className="flex-1 flex items-center justify-center px-12 pb-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {previewIndex > 0 && (
              <button
                onClick={() => setPreviewIndex((i) => (i ?? 0) - 1)}
                aria-label="Previous"
                className="absolute left-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-zinc-100 transition flex items-center justify-center"
              >
                <IconArrowLeft size={16} />
              </button>
            )}

            {previewing.contentType.startsWith('image/') ? (
              <img
                src={api.attachmentUrl(previewing.id)}
                alt={previewing.filename}
                className="max-w-full max-h-full rounded-md object-contain"
              />
            ) : (
              <video
                src={api.attachmentUrl(previewing.id)}
                controls
                autoPlay
                className="max-w-full max-h-full rounded-md"
              />
            )}

            {previewIndex < media.length - 1 && (
              <button
                onClick={() => setPreviewIndex((i) => (i ?? 0) + 1)}
                aria-label="Next"
                className="absolute right-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-zinc-100 transition flex items-center justify-center"
              >
                <IconChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
